import { z } from 'zod'

import { isCalendarDate } from '../utils/calendar-date'

/**
 * Data-migration source connectors ("Connect directly" on Admin → Data →
 * Bring your data). A connector run pulls a source account into staging,
 * lets an owner/admin review it (people map first), commits it, reports a
 * reconciliation, and can be rolled back for 14 days or finalized.
 *
 * Organization accounts only; gated by the IMPORT_CONNECTORS feature flag
 * (personal OFF). Source tokens are accepted once, stored encrypted and wiped
 * on finalize / cancel / rollback or 30 days after the run was created. They
 * are never returned by the API.
 */

/** Source systems with a connector design. Only `HARVEST` is built today. */
export const IMPORT_CONNECTOR_SYSTEMS = [
  'HARVEST',
  'PRODUCTIVE',
  'TEAMWORK',
  'CLOCKIFY',
  'SCORO',
] as const
export type ImportConnectorSystem = (typeof IMPORT_CONNECTOR_SYSTEMS)[number]

/** Connectors that accept runs. The rest stay "Coming soon". */
export const AVAILABLE_IMPORT_CONNECTOR_SYSTEMS: readonly ImportConnectorSystem[] =
  ['HARVEST']

export const IMPORT_SOURCE_RUN_STATUSES = [
  'PULL_QUEUED',
  'PULLING',
  'READY',
  'COMMIT_QUEUED',
  'COMMITTING',
  'COMMITTED',
  'ROLLBACK_QUEUED',
  'ROLLING_BACK',
  'ROLLED_BACK',
  'FINALIZED',
  'FAILED',
  'CANCELLED',
] as const
export type ImportSourceRunStatus = (typeof IMPORT_SOURCE_RUN_STATUSES)[number]

/** Runs the worker still has to act on or that await the admin. */
export const OPEN_IMPORT_SOURCE_RUN_STATUSES: readonly ImportSourceRunStatus[] =
  [
    'PULL_QUEUED',
    'PULLING',
    'READY',
    'COMMIT_QUEUED',
    'COMMITTING',
    'COMMITTED',
    'ROLLBACK_QUEUED',
    'ROLLING_BACK',
  ]

/** How long a committed run can be rolled back. */
export const IMPORT_SOURCE_ROLLBACK_DAYS = 14
/** Credentials are wiped no later than this many days after the run started. */
export const IMPORT_SOURCE_CREDENTIAL_TTL_DAYS = 30

/**
 * What a run brings over. People are always pulled (time needs an owner);
 * contacts need clients; time needs projects; budgets need projects;
 * invoices need clients. `rates` covers backdated member rate history and the
 * per-entry rates Harvest recorded on each hour.
 */
export const IMPORT_SOURCE_SCOPE_ITEMS = [
  'clients',
  'contacts',
  'projects',
  'time',
  'rates',
  'budgets',
  'invoices',
] as const
export type ImportSourceScopeItem = (typeof IMPORT_SOURCE_SCOPE_ITEMS)[number]

export const IMPORT_SOURCE_SCOPE_LABELS: Record<ImportSourceScopeItem, string> =
  {
    clients: 'Clients',
    contacts: 'Client contacts',
    projects: 'Projects',
    time: 'Time entries',
    rates: 'Rates',
    budgets: 'Project budgets',
    invoices: 'Invoices and payments',
  }

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date')
  .refine(isCalendarDate, {
    message: 'Use a real date',
  })

export const ImportSourceScopeSchema = z
  .object({
    include: z
      .array(z.enum(IMPORT_SOURCE_SCOPE_ITEMS))
      .min(1, 'Choose at least one thing to bring over')
      .max(IMPORT_SOURCE_SCOPE_ITEMS.length),
    /** Time entries and invoices on or after this date. */
    historyFrom: isoDate.optional(),
    /** Bring archived clients and projects too. */
    includeArchived: z.boolean().default(true),
  })
  .strict()
  .superRefine((scope, ctx) => {
    const has = (item: ImportSourceScopeItem) => scope.include.includes(item)
    const needs: [ImportSourceScopeItem, ImportSourceScopeItem][] = [
      ['contacts', 'clients'],
      ['projects', 'clients'],
      ['time', 'projects'],
      ['budgets', 'projects'],
      ['invoices', 'clients'],
    ]
    for (const [item, requirement] of needs) {
      if (has(item) && !has(requirement)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['include'],
          message: `${IMPORT_SOURCE_SCOPE_LABELS[item]} need ${IMPORT_SOURCE_SCOPE_LABELS[requirement].toLowerCase()}`,
        })
      }
    }
  })

export type ImportSourceScope = z.infer<typeof ImportSourceScopeSchema>

/**
 * Harvest personal access token + account id. The token is sent once over
 * HTTPS, validated against Harvest, encrypted at rest and never echoed back.
 */
export const ConnectHarvestSchema = z
  .object({
    accessToken: z
      .string()
      .trim()
      .min(20, 'Paste the full personal access token')
      .max(512)
      .regex(/^[A-Za-z0-9._-]+$/, 'That does not look like a Harvest token'),
    accountId: z
      .string()
      .trim()
      .regex(/^\d{1,20}$/, 'The Harvest account ID is a number'),
    scope: ImportSourceScopeSchema,
  })
  .strict()

export type ConnectHarvest = z.infer<typeof ConnectHarvestSchema>

export const IMPORT_SOURCE_PEOPLE_ACTIONS = ['MATCH', 'STUB', 'SKIP'] as const
export type ImportSourcePeopleAction =
  (typeof IMPORT_SOURCE_PEOPLE_ACTIONS)[number]

/** One source person and what the commit will do with them. */
export interface ImportSourcePerson {
  externalId: string
  name: string
  email: string | null
  isActive: boolean
  isContractor: boolean
  /** Hours this person logged in the pulled range. */
  hours: number
  /**
   * MATCH → an existing active member owns their time; STUB → a deactivated,
   * non-billable member that uses no seat; SKIP → their time is not imported.
   */
  action: ImportSourcePeopleAction
  memberId: string | null
  /** Member matched by email when the run was pulled, if any. */
  suggestedMemberId: string | null
}

export const UpdateImportSourcePeopleSchema = z
  .object({
    people: z
      .array(
        z
          .object({
            externalId: z.string().min(1).max(64),
            action: z.enum(IMPORT_SOURCE_PEOPLE_ACTIONS),
            memberId: z.string().uuid().nullable().optional(),
          })
          .strict()
          .refine(entry => entry.action !== 'MATCH' || !!entry.memberId, {
            message: 'Choose the member to match',
            path: ['memberId'],
          }),
      )
      .min(1)
      .max(5000),
  })
  .strict()

export type UpdateImportSourcePeople = z.infer<
  typeof UpdateImportSourcePeopleSchema
>

export interface ImportSourceKindCount {
  staged: number
  committed: number
  skipped: number
  failed: number
}

export interface ImportSourceReconciliationRow {
  person: string
  project: string
  /** YYYY-MM */
  month: string
  sourceHours: number
  importedHours: number
  varianceHours: number
}

export interface ImportSourceReconciliation {
  generatedAt: string
  sourceHours: number
  importedHours: number
  varianceHours: number
  /** Hours intentionally not imported (people set to SKIP). */
  skippedHours: number
  /** Only rows with a variance, largest first, capped. */
  rows: ImportSourceReconciliationRow[]
  /** Hours linked to an invoice imported in the same run. */
  linkedToImportedInvoiceHours: number
  /** Hours invoiced in the source but whose invoice was not imported. */
  billedExternallyHours: number
  invoices: { source: number; imported: number }
}

export interface ImportSourceWarning {
  kind: string
  message: string
}

export interface ImportSourceRunSummary {
  id: string
  system: ImportConnectorSystem
  sourceAccountId: string
  sourceAccountName: string | null
  status: ImportSourceRunStatus
  scope: ImportSourceScope
  counts: Record<string, ImportSourceKindCount>
  warnings: ImportSourceWarning[]
  error: string | null
  /** For a FAILED run: the step it stopped in (retry resumes that step). */
  failedPhase: 'PULL' | 'COMMIT' | 'ROLLBACK' | null
  /** True while an encrypted token is still stored for this run. */
  hasCredentials: boolean
  credentialsWipedAt: string | null
  reconciliation: ImportSourceReconciliation | null
  createdAt: string
  pulledAt: string | null
  committedAt: string | null
  rollbackUntil: string | null
  rolledBackAt: string | null
  finalizedAt: string | null
}

export type ImportConnectorsAvailabilityReason =
  | 'available'
  | 'personal'
  | 'feature_disabled'

export interface ImportSourceRunsResponse {
  available: boolean
  reason: ImportConnectorsAvailabilityReason
  runs: ImportSourceRunSummary[]
}

export interface ImportSourceException {
  kind: string
  externalId: string
  status: 'SKIPPED' | 'FAILED'
  /** A vetted explanation; raw payloads and internal error text stay private. */
  reason: string
}

export interface ImportSourceExceptionsResponse {
  items: ImportSourceException[]
  nextCursor: string | null
}

/** An active member with a login — the only valid MATCH target. */
export interface ImportSourceMatchableMember {
  id: string
  name: string
  email: string | null
}

export interface ImportSourcePeopleResponse {
  people: ImportSourcePerson[]
  members: ImportSourceMatchableMember[]
}

export function getImportConnectorSystemLabel(system: string): string {
  switch (system) {
    case 'HARVEST':
      return 'Harvest'
    case 'PRODUCTIVE':
      return 'Productive'
    case 'TEAMWORK':
      return 'Teamwork'
    case 'CLOCKIFY':
      return 'Clockify'
    case 'SCORO':
      return 'Scoro'
    default:
      return system
  }
}
